import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, FAQSection, CTASection, FeatureList } from '../../components/PageTemplate';
import { 
  FileText, Shield, CheckCircle, Clock, Upload, 
  AlertTriangle, Star 
} from 'lucide-react';

export default function ContractorsGettingStarted() {
  const applicationSteps = [
    {
      title: 'Create Account',
      description: 'Basic details: name, email, phone, location, and the trades you cover.'
    },
    {
      title: 'Upload Documents',
      description: 'Public liability insurance, trade certifications (Gas Safe, NICEIC, etc.), and ID.'
    },
    {
      title: 'Set Your Profile',
      description: 'Service area, availability, typical response times, and your rates.'
    },
    {
      title: 'Verification Review',
      description: 'We verify your documents and check for any complaints or issues.'
    },
    {
      title: 'Approval',
      description: 'Once approved, you\'re live and can start receiving job notifications.'
    }
  ];

  const requirements = [
    {
      icon: Shield,
      title: 'Public Liability Insurance',
      description: 'Minimum £1 million cover required. Upload your certificate.'
    },
    {
      icon: FileText,
      title: 'Trade Certifications',
      description: 'Gas Safe, NICEIC, OFTEC, etc. depending on your trade.'
    },
    {
      icon: CheckCircle,
      title: 'ID Verification',
      description: 'Passport or driving license to confirm your identity.'
    },
    {
      icon: Star,
      title: 'References',
      description: 'We may request references from previous clients.'
    }
  ];

  const faqs = [
    {
      question: 'How long does approval take?',
      answer: 'Typically 3-5 business days. It may take longer if we need to verify certifications or follow up on any issues.'
    },
    {
      question: 'What if I don\'t have trade certifications?',
      answer: 'Some trades (handyman, cleaning, gardening) don\'t require formal certification. Others (gas, electrical) are legally required. We\'ll tell you what you need.'
    },
    {
      question: 'Is there a fee to join?',
      answer: 'No. Joining the network is free. We take a commission (10-15%) on completed jobs.'
    },
    {
      question: 'Can I cover multiple trades?',
      answer: 'Yes. List all the services you offer. You\'ll need relevant certification for each regulated trade.'
    },
    {
      question: 'What if my application is rejected?',
      answer: 'We\'ll tell you why. Common reasons: missing insurance, expired certifications, unresolved complaints. Fix the issue and reapply.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="GETTING STARTED"
        title="Join Our Contractor"
        subtitle="Network"
        description="A simple application process to get you verified and receiving jobs. We vet all contractors to maintain quality."
        primaryCTA={{ href: '/contractor/register', label: 'Start Application' }}
        secondaryCTA={{ href: '/contractors', label: 'Back to Overview' }}
        gradient="from-amber-50 via-white to-slate-50"
      />

      <BenefitsGrid title="What You'll Need" benefits={requirements} />

      <HowItWorks title="Application Process" steps={applicationSteps} />

      {/* Certification Examples */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Required Certifications by Trade
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { trade: 'Gas Engineer', certs: ['Gas Safe Registration'] },
              { trade: 'Electrician', certs: ['NICEIC, NAPIT, or ELECSA'] },
              { trade: 'Plumber', certs: ['No mandatory cert (recommended: WaterSafe)'] },
              { trade: 'Oil Engineer', certs: ['OFTEC Registration'] },
              { trade: 'Locksmith', certs: ['MLA or ALOA (recommended)'] },
              { trade: 'Asbestos', certs: ['UKATA or equivalent'] },
            ].map((item, index) => (
              <div key={index} className="p-4 bg-slate-50 rounded-xl">
                <h3 className="font-semibold mb-2">{item.trade}</h3>
                <p className="text-sm text-slate-600">{item.certs.join(', ')}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-500 mt-6">
            All contractors need Public Liability Insurance regardless of trade.
          </p>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to join?"
        description="Start your application and get verified in just a few days."
        primaryCTA={{ href: '/contractor/register', label: 'Apply Now' }}
      />
    </PublicLayout>
  );
}
